import JSZip from 'jszip';
import { toGpx, messagesToCsv, eventsToCsv, safeFileName } from './formats.js';
import { buildManifest, verificationInstructions } from './manifest.js';
import { verifyPageHtml } from './verifyPage.js';
import { evidenceReadmeHtml } from './readme.js';

function base64ToBytes(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

const stamp = epochMs => new Date(epochMs).toISOString().replace(/[:.]/g, '-');

/** Fichiers texte toujours présents (trajets, messages, événements). */
export function textParts(bundle) {
    const parts = [{ path: 'trajets/tous-les-participants.gpx', content: toGpx(bundle), description: 'Trajets de tous les participants (GPX 1.1)' }];
    (bundle?.tracks || []).forEach(track => {
        if (!track.positions?.length) return;
        parts.push({
            path: `trajets/${safeFileName(track.label, `participant-${track.id}`)}.gpx`,
            content: toGpx(bundle, { trackIds: [track.id] }),
            description: `Trajet de ${track.label} (GPX 1.1)`,
        });
    });
    // Une session d'alerte n'a pas de messagerie : pas de fichier vide.
    if (bundle?.kind !== 'SESSION' || (bundle?.messages || []).length) {
        parts.push({ path: 'messages.csv', content: messagesToCsv(bundle), description: 'Messages échangés (CSV, séparateur ;)' });
    }
    parts.push({
        path: 'evenements.csv',
        content: eventsToCsv(bundle),
        description: bundle?.kind === 'SESSION'
            ? 'Journal de la session : chaque événement reçu, dans l’ordre (CSV, séparateur ;)'
            : 'Chronologie des événements (CSV, séparateur ;)',
    });
    return parts;
}

/**
 * Assemble le dossier complet : trajets, messages, événements, photos
 * originales, médias fournis par l'application, manifeste SHA-256 et
 * procédure de vérification.
 *
 * @param mediaExporters [{ label, fileName, fetch: async () => Blob }]
 */
export async function buildEvidenceZip({ bundle, fetchBlob, mediaExporters = [], source = '', onProgress = () => {}, evidence = null, context: baseContext = {} }) {
    const files = textParts(bundle);
    let context = baseContext;
    let registre = null;

    // Registre de preuve et jetons d'horodatage (si fournis par l'application).
    if (evidence) {
        onProgress('Registre de preuve');
        const provided = typeof evidence === 'function' ? await evidence() : evidence;
        // Soit le registre seul, soit { registre, files } (originaux chiffrés, clé de lecture…).
        registre = provided?.registre || (provided?.entries ? provided : null);
        (provided?.files || []).forEach(file => files.push(file));
        // Ce que seul le fournisseur connaît (nombre de témoins…) complète le guide.
        if (provided?.context) context = { ...context, ...provided.context };
        if (registre) {
            files.push({ path: 'preuve/registre.json', content: `${JSON.stringify(registre, null, 2)}\n`,
                description: 'Registre de preuve chaîné (empreintes des données chiffrées reçues)' });
            const REASONS = { ALERT: 'déclenchement de l’alerte', START: 'début du rendez-vous', PERIODIC: 'en cours, toutes les 10 min', END: 'fin' };
            (registre.anchors || []).forEach((anchor, index) => {
                if (!anchor.tsaResponse) return;
                const provider = String(anchor.tsaProvider || 'autorite').replace(/^https?:\/\//, '').replace(/[^a-z0-9.-]+/gi, '-');
                files.push({
                    path: `preuve/ancrages/ancrage-${String(index + 1).padStart(2, '0')}-${anchor.upToSeq}-${provider}${anchor.qualified ? '-qualifie' : ''}.tsr`,
                    content: base64ToBytes(anchor.tsaResponse),
                    description: `Jeton d'horodatage RFC 3161 ${anchor.qualified ? 'qualifié eIDAS ' : ''}(${anchor.tsaProvider || 'autorité'}, ${REASONS[anchor.anchorReason] || 'ancrage'}) : racine de l'arbre de la session jusqu'à l'entrée ${anchor.upToSeq}`,
                });
            });
        }
    }
    files.push({ path: 'verify.html', content: verifyPageHtml(), description: 'Vérification hors ligne du dossier (navigateur)' });

    const photos = (bundle?.tracks || []).flatMap(track => (track.photos || []).map(photo => ({ ...photo, owner: track.label })));
    for (const [index, photo] of photos.entries()) {
        onProgress(`Photo ${index + 1}/${photos.length}`);
        const blob = await fetchBlob(photo.url);
        files.push({
            path: `photos/${stamp(photo.t)}_${safeFileName(photo.owner)}_${safeFileName(photo.fileName, 'photo.jpg')}`,
            content: blob,
            description: `Photo prise par ${photo.owner}`,
        });
    }

    for (const [index, exporter] of mediaExporters.entries()) {
        onProgress(`${exporter.label} (${index + 1}/${mediaExporters.length})`);
        const blob = await exporter.fetch(message => onProgress(`${exporter.label} : ${message}`));
        files.push({ path: `medias/${safeFileName(exporter.fileName, 'media.mp4')}`, content: blob, description: exporter.label });
    }

    onProgress('Calcul des empreintes');
    const { manifest, manifestJson, entries, manifestSha256 } = await buildManifest({ bundle, files, source });
    const zip = new JSZip();
    entries.forEach(entry => zip.file(entry.path, entry.bytes));
    zip.file('manifest.json', manifestJson);
    zip.file('VERIFIER.txt', verificationInstructions(manifest, manifestSha256));
    // Guide pour l'enquêteur ou l'avocat : ce que contient le dossier et comment le vérifier.
    zip.file('LISEZ-MOI.html', evidenceReadmeHtml({ manifest, manifestSha256, registre, context }));

    onProgress('Compression');
    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
    return { blob, manifest, manifestSha256, fileName: `dossier-${safeFileName(bundle?.title, 'activite')}-${stamp(Date.now())}.zip` };
}

export function saveBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
